import { getEvaluationRules } from '../../evaluation-rules-load';
import type { PostureOrientationClassificationStrategy } from './posture-orientation-strategy-interface';
import type {
  PostureClassificationStrategyContext,
  PostureClassificationStrategyInput,
  PostureClassificationStrategyResult,
  PostureOrientationLabel,
} from './posture-orientation-strategy-types';
import { parseLateralPostureMetrics } from './posture-analysis-metrics-parse';
import {
  sagittalFromLandmarks,
  scoreApt,
  scoreSwayback,
} from './hip-displacement-horizontal-strategy';
import { logPostureClassificationDebug } from './posture-classification-debug';
import type { Landmark } from '@/components/posture-analysis/types';
import { IDX } from '@/components/posture-analysis/constants';

const STRATEGY_ID = 'indicatorPointScoreVpr';
const STRATEGY_NAME = 'Indicator Point score, Visual Pattern Recognition Strategy';
const DEBUG_LOG_PREFIX = '[indicatorPointScoreVpr]';

const MAX_AUTO_POINTS = 7;

interface VetoThresholds {
  slrSwaybackVetoMaxDeg: number;
  kneeFlexionExtremeDegMin: number;
  fullHipExtensionDeg: number;
  oberNeutralDeg: number;
  kneeHyperextensionExtremeDegMin: number;
  pelvicTiltVetoDegMin: number;
  overridePointBoost: number;
  indicatorPointWeight: number;
}

const DEFAULT_VETO_THRESHOLDS: VetoThresholds = {
  slrSwaybackVetoMaxDeg: 45,
  kneeFlexionExtremeDegMin: 10,
  fullHipExtensionDeg: 0,
  oberNeutralDeg: 0,
  kneeHyperextensionExtremeDegMin: 5.0,
  pelvicTiltVetoDegMin: 15.0,
  overridePointBoost: 3,
  indicatorPointWeight: 4,
};

function readVetoThresholds(rules: any): VetoThresholds {
  const v = rules?.vetoExclusion ?? {};
  return {
    slrSwaybackVetoMaxDeg: v.slrSwaybackVetoMaxDeg ?? DEFAULT_VETO_THRESHOLDS.slrSwaybackVetoMaxDeg,
    kneeFlexionExtremeDegMin:
      v.kneeFlexionExtremeDegMin ?? DEFAULT_VETO_THRESHOLDS.kneeFlexionExtremeDegMin,
    fullHipExtensionDeg: v.fullHipExtensionDeg ?? DEFAULT_VETO_THRESHOLDS.fullHipExtensionDeg,
    oberNeutralDeg: v.oberNeutralDeg ?? DEFAULT_VETO_THRESHOLDS.oberNeutralDeg,
    kneeHyperextensionExtremeDegMin:
      v.kneeHyperextensionExtremeDegMin ?? DEFAULT_VETO_THRESHOLDS.kneeHyperextensionExtremeDegMin,
    pelvicTiltVetoDegMin: v.pelvicTiltVetoDegMin ?? DEFAULT_VETO_THRESHOLDS.pelvicTiltVetoDegMin,
    overridePointBoost: v.overridePointBoost ?? DEFAULT_VETO_THRESHOLDS.overridePointBoost,
    indicatorPointWeight: v.indicatorPointWeight ?? DEFAULT_VETO_THRESHOLDS.indicatorPointWeight,
  };
}

const sumDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) : null;
};

const avgDefined = (...vals: (number | null | undefined)[]): number | null => {
  const finite = vals.filter((v): v is number => typeof v === 'number' && isFinite(v));
  return finite.length ? finite.reduce((a, b) => a + b, 0) / finite.length : null;
};

const rad2deg = (r: number) => (r * 180) / Math.PI;

function angleAtLandmark(a: Landmark, b: Landmark, c: Landmark): number {
  if (!a || !b || !c) return NaN;
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const mab = Math.hypot(ab.x, ab.y);
  const mcb = Math.hypot(cb.x, cb.y);
  if (mab === 0 || mcb === 0) return NaN;
  const cos = Math.min(1, Math.max(-1, dot / (mab * mcb)));
  return rad2deg(Math.acos(cos));
}

interface AugmentedScore {
  pts: number;
  evidence: string[];
  activeSignals: string[];
}

function augmentScores(
  input: PostureClassificationStrategyInput,
  context: PostureClassificationStrategyContext
): {
  sb: AugmentedScore;
  apt: AugmentedScore;
  dynamicMax: number;
  parsed: any;
  fromLm: any;
  rules: any;
} | null {
  const rules = (getEvaluationRules() as any).posture.thresholds;
  const kneeThresholds = {
    kneeFlexionDegMin: rules.kneeFlexionDegMin,
    kneeHyperextensionDegMin: rules.kneeHyperextensionDegMin,
  };
  const parsed = parseLateralPostureMetrics(input.analysisData, kneeThresholds);
  if (!parsed) return null;

  const avgCvAngle = parsed.avgCvAngle;
  const tibialInclination = parsed.tibialInclination;
  const pelvicTiltDegMin =
    input.clientSex === 'Female' ? rules.pelvicTiltDegMinFemale : rules.pelvicTiltDegMinMale;

  const fromLm = sagittalFromLandmarks(
    input,
    context,
    kneeThresholds.kneeFlexionDegMin,
    kneeThresholds.kneeHyperextensionDegMin
  );
  const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
  const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
  const kneeHyperext = fromLm ? fromLm.kneeHyperext : parsed.isKneeHyperext && !parsed.isKneeNeutral;
  const kneeFlex = fromLm ? fromLm.kneeFlex : parsed.isKneeFlexed;
  const pelvicTilt = fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;
  const kneeFlexEvidenceDeg =
    fromLm?.kneeFlexEvidenceDeg ??
    (kneeFlex && isFinite(parsed.maxKneeFlexDeg) ? parsed.maxKneeFlexDeg : undefined);
  const kneeHipAntCm =
    fromLm && isFinite(fromLm.kneeHipAntCm) ? fromLm.kneeHipAntCm : parsed.kneeHipAntCm;

  const sbBase = scoreSwayback(
    hipFwdCm,
    torsoOffsetCm,
    kneeFlex,
    pelvicTilt,
    rules,
    kneeFlexEvidenceDeg,
    kneeHipAntCm,
    pelvicTiltDegMin
  );
  const aptBase = scoreApt(
    hipFwdCm,
    torsoOffsetCm,
    kneeHyperext,
    pelvicTilt,
    rules,
    input.clientSex,
    kneeHipAntCm
  );

  let sbPts = sbBase.pts;
  let aptPts = aptBase.pts;
  const sbEvidence: string[] = [...sbBase.evidence];
  const aptEvidence: string[] = [...aptBase.evidence];
  const sbSignals: string[] = [...sbBase.activeSignals];
  const aptSignals: string[] = [...aptBase.activeSignals];

  // --- New auto signals: Forward Head Posture ---
  const fhpMin = rules.fhpSwaybackDegMin ?? 10;
  if (isFinite(avgCvAngle) && avgCvAngle >= fhpMin) {
    sbPts += 1;
    sbSignals.push('vpr.sb.forwardHeadPosture');
    sbEvidence.push(
      `[+1] Forward head posture (CV angle ~${avgCvAngle.toFixed(1)}° ≥ ${fhpMin}°; typical swayback presentation)`
    );
  } else if (
    isFinite(avgCvAngle) &&
    avgCvAngle < fhpMin &&
    isFinite(pelvicTilt) &&
    pelvicTilt >= pelvicTiltDegMin
  ) {
    aptPts += 1;
    aptSignals.push('vpr.apt.neutralHeadPosture');
    aptEvidence.push(
      `[+1] Neutral/mild head posture (CV ~${avgCvAngle.toFixed(1)}° < ${fhpMin}°) with anterior pelvic tilt (~${pelvicTilt.toFixed(1)}°); supports APT presentation`
    );
  }

  // --- New auto signals: Tibial Inclination ---
  const tibPostMin = rules.tibialPostThresholdDeg ?? 3;
  const tibAntMin = rules.tibialAntThresholdDeg ?? 3;
  if (isFinite(tibialInclination) && tibialInclination <= -tibPostMin) {
    sbPts += 1;
    sbSignals.push('vpr.sb.posteriorTibialInclination');
    sbEvidence.push(
      `[+1] Posterior tibial inclination (~${tibialInclination.toFixed(1)}° ≤ −${tibPostMin}°; knee flexion compensation pattern)`
    );
  } else if (isFinite(tibialInclination) && tibialInclination >= tibAntMin) {
    aptPts += 1;
    aptSignals.push('vpr.apt.anteriorTibialInclination');
    aptEvidence.push(
      `[+1] Anterior tibial inclination (~${tibialInclination.toFixed(1)}° ≥ ${tibAntMin}°; hyperextension compensation pattern)`
    );
  }

  // --- Practitioner visual observations (optional toggles) ---
  const obs = input.practitionerObservations;
  let toggledCount = 0;

  if (obs?.poochBelly) {
    toggledCount += 1;
    sbPts += 1;
    sbSignals.push('vpr.sb.obs.poochBelly');
    sbEvidence.push('[+1] Practitioner observation: pooch belly (swayback indicator)');
  }
  if (obs?.flatGlutes) {
    toggledCount += 1;
    sbPts += 1;
    sbSignals.push('vpr.sb.obs.flatGlutes');
    sbEvidence.push('[+1] Practitioner observation: flat/absent glutes (swayback indicator)');
  }
  if (obs?.ribFlare) {
    toggledCount += 1;
    aptPts += 1;
    aptSignals.push('vpr.apt.obs.ribFlare');
    aptEvidence.push('[+1] Practitioner observation: rib flare (APT indicator)');
  }
  if (obs?.flatCervicalCurve) {
    toggledCount += 1;
    aptPts += 1;
    aptSignals.push('vpr.apt.obs.flatCervicalCurve');
    aptEvidence.push(
      '[+1] Practitioner observation: flat/reduced cervical curve (APT indicator)'
    );
  }

  const dynamicMax = MAX_AUTO_POINTS + toggledCount;

  return {
    sb: {
      pts: Math.min(dynamicMax, sbPts),
      evidence: sbEvidence,
      activeSignals: sbSignals,
    },
    apt: {
      pts: Math.min(dynamicMax, aptPts),
      evidence: aptEvidence,
      activeSignals: aptSignals,
    },
    dynamicMax,
    parsed,
    fromLm,
    rules,
  };
}

export const indicatorPointScoreVprStrategy: PostureOrientationClassificationStrategy = {
  id: STRATEGY_ID,
  displayName: STRATEGY_NAME,
  maxClassificationPoints: MAX_AUTO_POINTS,

  classify(
    input: PostureClassificationStrategyInput,
    context: PostureClassificationStrategyContext
  ): PostureClassificationStrategyResult | null {
    const rulesBundle = getEvaluationRules() as any;
    const rules = rulesBundle.posture.thresholds;
    const veto = readVetoThresholds(rules);

    const augmented = augmentScores(input, context);
    if (!augmented) return null;

    const { sb, apt, dynamicMax, parsed, fromLm } = augmented;

    // Extract resolved lateral parameters
    const hipFwdCm = fromLm?.hipFwdCm ?? parsed.hipFwdVal;
    const torsoOffsetCm = fromLm?.torsoOffsetCm ?? parsed.sagittalTorsoOffset;
    const kneeHyperext = fromLm ? fromLm.kneeHyperext : parsed.isKneeHyperext && !parsed.isKneeNeutral;
    const kneeFlex = fromLm ? fromLm.kneeFlex : parsed.isKneeFlexed;
    const pelvicTilt = fromLm && isFinite(fromLm.pelvicTilt) ? fromLm.pelvicTilt : parsed.pelvicTilt;
    const kneeFlexDeg =
      fromLm?.kneeFlexEvidenceDeg ??
      (kneeFlex && isFinite(parsed.maxKneeFlexDeg) ? parsed.maxKneeFlexDeg : NaN);

    let kneeHyperextDeg = NaN;
    if (fromLm && input.sagittal?.landmarks?.length) {
      const s = input.sagittal;
      const side = s.sideView;
      const idxLR = (l: number, r: number) => (side === 'Left' ? l : r);
      const H = s.landmarks[idxLR(IDX.L_HIP, IDX.R_HIP)];
      const K = s.landmarks[idxLR(IDX.L_KNEE, IDX.R_KNEE)];
      const A = s.landmarks[idxLR(IDX.L_ANK, IDX.R_ANK)];
      if (H && K && A && Math.abs(A.y - H.y) > 10) {
        const kneeRawAngle = angleAtLandmark(H, K, A);
        const kneeDeg = isFinite(kneeRawAngle) ? Math.abs(180 - kneeRawAngle) : NaN;
        kneeHyperextDeg = kneeDeg;
      }
    }
    if (!isFinite(kneeHyperextDeg)) {
      kneeHyperextDeg = parsed.maxKneeHyperextDeg;
    }

    const isPinned =
      !!context.asisPsisLine ||
      (!!input.sagittal?.custom?.ASIS && !!input.sagittal?.custom?.PSIS) ||
      (isFinite(pelvicTilt) && !parsed.isPelvicTiltEstimated);

    // Compute indicators (no hard exclusions, everything adds indicatorPointWeight points)
    const indicatorWeight = veto.indicatorPointWeight;
    let sbIndicatorPoints = 0;
    let aptIndicatorPoints = 0;
    const indicatorEvidence: string[] = [];
    let evaluableIndicatorsCount = 0;

    // 1. Extreme Knee Flexion & Hyperextension indicators
    const isKneeStateEvaluable =
      isFinite(parsed.maxKneeFlexDeg) || isFinite(parsed.maxKneeHyperextDeg);
    if (isKneeStateEvaluable) {
      evaluableIndicatorsCount++;
      if (kneeFlex && isFinite(kneeFlexDeg) && kneeFlexDeg > veto.kneeFlexionExtremeDegMin) {
        sbIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] Extreme knee flexion (~${kneeFlexDeg.toFixed(0)}° > ${veto.kneeFlexionExtremeDegMin}°) → +${indicatorWeight} Swayback`
        );
      }
      if (kneeHyperext && isFinite(kneeHyperextDeg) && kneeHyperextDeg >= veto.kneeHyperextensionExtremeDegMin) {
        aptIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] Extreme knee hyperextension (~${kneeHyperextDeg.toFixed(0)}° ≥ ${veto.kneeHyperextensionExtremeDegMin}°) → +${indicatorWeight} APT`
        );
      }
    }

    // 2. Pelvic tilt ≥ 15° (pinned ASIS/PSIS only)
    const isPelvicTiltEvaluable = isPinned && isFinite(pelvicTilt);
    if (isPelvicTiltEvaluable) {
      evaluableIndicatorsCount++;
      if (pelvicTilt >= veto.pelvicTiltVetoDegMin) {
        aptIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] Pelvic tilt ≥ ${veto.pelvicTiltVetoDegMin}° with pinned ASIS/PSIS (~${pelvicTilt.toFixed(1)}°) → +${indicatorWeight} APT`
        );
      }
    }

    // SoP-based indicators
    const sop = input.sopMetrics;

    // 3. Femoral rotation asymmetry (R2/R3)
    const irTotal = sop ? sumDefined(sop.femoralIrLeft, sop.femoralIrRight) : null;
    const erTotal = sop ? sumDefined(sop.femoralErLeft, sop.femoralErRight) : null;
    const isFemoralRotationEvaluable = irTotal !== null && erTotal !== null;
    if (isFemoralRotationEvaluable) {
      evaluableIndicatorsCount++;
      if (irTotal! > erTotal!) {
        aptIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] SoP: femoral IR (${irTotal!.toFixed(0)}°) > ER (${erTotal!.toFixed(0)}°) → +${indicatorWeight} APT`
        );
      } else if (erTotal! > irTotal!) {
        sbIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] SoP: femoral ER (${erTotal!.toFixed(0)}°) > IR (${irTotal!.toFixed(0)}°) → +${indicatorWeight} Swayback`
        );
      }
    }

    // R4 — high SLR excludes Swayback (does not vote APT).
    let slrExcludesSwayback = false;
    if (sop) {
      const avgSlr = avgDefined(sop.slrLeft, sop.slrRight);
      if (avgSlr !== null && avgSlr > veto.slrSwaybackVetoMaxDeg) {
        slrExcludesSwayback = true;
        indicatorEvidence.push(
          `[VETO] SoP: SLR (${avgSlr.toFixed(0)}°) > ${veto.slrSwaybackVetoMaxDeg}° → excludes Swayback`
        );
      }
    }

    // 5. Torso offset (lean)
    const isTorsoOffsetEvaluable = isFinite(torsoOffsetCm);
    if (isTorsoOffsetEvaluable) {
      evaluableIndicatorsCount++;
      if (torsoOffsetCm > rules.strictLean) {
        aptIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] Image: shoulders forward of hips (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm > ${rules.strictLean} cm) → +${indicatorWeight} APT`
        );
      } else if (torsoOffsetCm < -rules.strictLean) {
        sbIndicatorPoints += indicatorWeight;
        indicatorEvidence.push(
          `[INDICATOR] Image: shoulders behind hips / “C” curve (Δ ≈ ${torsoOffsetCm.toFixed(1)} cm < -${rules.strictLean} cm) → +${indicatorWeight} Swayback`
        );
      }
    }

    // Apply points
    let finalScoreSwayback = sb.pts + sbIndicatorPoints;
    const finalScoreApt = apt.pts + aptIndicatorPoints;
    if (slrExcludesSwayback) {
      finalScoreSwayback = 0;
    }
    const indicatorMaxPoints = evaluableIndicatorsCount * indicatorWeight;
    const maxPoints = dynamicMax + indicatorMaxPoints;

    // Decision logic (same tie-breaking as Strategy 1)
    let orientation: PostureOrientationLabel = 'Stacked';
    let matched = 0;
    let winnerEvidence: string[] = [];
    let decisionPath = '';

    if (finalScoreSwayback === 0 && finalScoreApt === 0) {
      orientation = 'Stacked';
      matched = 0;
      winnerEvidence = [];
      decisionPath = 'noPoints.stacked';
    } else if (finalScoreSwayback > finalScoreApt) {
      orientation = 'Swayback';
      matched = finalScoreSwayback;
      winnerEvidence = sb.evidence;
      decisionPath = 'sbWinner';
    } else if (finalScoreApt > finalScoreSwayback) {
      orientation = 'Anterior Pelvic Tilt';
      matched = finalScoreApt;
      winnerEvidence = apt.evidence;
      decisionPath = 'aptWinner';
    } else {
      // Tie-breaker
      if (hipFwdCm >= rules.hipForwardCmMin && finalScoreSwayback > 0) {
        orientation = 'Swayback';
        matched = finalScoreSwayback;
        winnerEvidence = [...sb.evidence, 'Tie-break: large hip–ankle offset favors swayback shift.'];
        decisionPath = 'tieBreak.hipFwdFavorsSwayback';
      } else {
        orientation = 'Anterior Pelvic Tilt';
        matched = finalScoreApt;
        winnerEvidence = [...apt.evidence, 'Tie-break: hip stack not dominant → anterior orientation.'];
        decisionPath = 'tieBreak.hipStackFavorsApt';
      }
    }

    const evidence = [...indicatorEvidence, ...winnerEvidence];

    const confidencePct =
      orientation === 'Stacked'
        ? Math.min(
            100,
            Math.round(
              (1 - Math.max(finalScoreSwayback, finalScoreApt) / Math.max(1, maxPoints * 2)) *
                100
            )
          )
        : Math.min(100, Math.round((matched / maxPoints) * 100));

    logPostureClassificationDebug(DEBUG_LOG_PREFIX, 'indicatorPointScoreVpr classification', {
      rawScoreSwayback: sb.pts,
      rawScoreApt: apt.pts,
      sbIndicatorPoints,
      aptIndicatorPoints,
      slrExcludesSwayback,
      finalScoreSwayback,
      finalScoreApt,
      orientation,
      decisionPath,
      hasSopMetrics: !!input.sopMetrics,
      indicatorEvidence,
    });

    return {
      orientation,
      confidencePct,
      matchedPoints: matched,
      maxPoints,
      strategyId: STRATEGY_ID,
      strategyDisplayName: STRATEGY_NAME,
      evidence,
    };
  },
};
